import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FileType, FormMode, QuoteFileIdType } from 'enums';
import { useBoolean, useSearchStateComplianceByState, useSearchUserMarket } from 'hooks';
import { InsuranceProduct, Suggestion, UserMarket } from 'types';
import { getFileType } from 'utils';
import { QuoteFormRoutes } from 'broker/broker-routes';
import { ExtendedBoxItem } from 'broker/components/FilesExplorer/types';
import { AIReason } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIReason';
import InnerPageLayout from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/InnerPageLayout';
import useLoadFilesSelection from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/Files/useLoadFilesSelection';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useUiStoreState } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import { quoteFilesTypes } from 'broker/utils/quote-utils';
import QuoteFooter from './components/QuoteFooter';
import QuoteFormFields from './components/QuoteFormFields';
import { InsightPopulatedByAI, QuoteFormProps } from './components/types';
import useSubmitQuoteForm from './components/useSubmitQuoteForm';
import { QuoteConfig } from './config';
import { DetailsFieldsNames } from './form-methods/enums';
import { FlowGenerationStatus, QuoteLocationState } from './types';
import { useGetQuoteData } from './useGetQuoteData';

export interface QuoteContentProps {
  locationState: QuoteLocationState;
  setIsDirty: (isDirty: boolean) => void;
  fileType: QuoteFileIdType;
  quoteConfig: QuoteConfig;
  currentStep: QuoteFormRoutes;
  selectedFiles: ExtendedBoxItem[];
  onClose(): void;
  resetFilesExplorerSelectionOnUnMount?: boolean;
  isDirty: boolean;
  appliedSuggestions: Record<string, Suggestion>;
  teamProductsLoading: boolean;
  products: InsuranceProduct[];
  initialProductId?: string;
}

export default function QuoteContent({
  locationState,
  isDirty,
  setIsDirty,
  onClose,
  fileType,
  initialProductId,
  products,
  appliedSuggestions,
  teamProductsLoading,
  quoteConfig,
  currentStep,
  selectedFiles,
  resetFilesExplorerSelectionOnUnMount = true,
}: QuoteContentProps) {
  const {
    layers,
    submission,
    markets,
    quotes,
    addSubmissionMarket,
    createQuote,
    updateQuote,
    createLayer,
    createMarketIssuingCompany,
    updateSubmissionMarketRequest,
  } = useSubmissionsWorkspace();
  const state = locationState;
  const { items: userMarkets, isLoading: userMarketsLoading } = useSearchUserMarket({
    filter: {},
  });

  const { watch } = useFormContext();

  // undefined means error
  const [flowGenerationStatus, setFlowGenerationStatus] = useState<FlowGenerationStatus>(FlowGenerationStatus.Idle);

  const [insightPopulatedByAI, setInsightPopulatedByAI] = useState<InsightPopulatedByAI | undefined>();
  const { mode, selectedQuote } = state;
  const { selectedLayer } = useGetQuoteData({
    layer: state?.layer,
    selectedQuote: state?.selectedQuote,
    mode,
  });

  const preSelectedFileIds = useMemo(() => selectedQuote?.[fileType], [fileType, selectedQuote]);
  useLoadFilesSelection({
    setIsDirty,
    preSelectedFileIds: locationState?.quoteFileIds || preSelectedFileIds,
    resetOnUnMount: resetFilesExplorerSelectionOnUnMount,
    viewOnlyMode: mode !== FormMode.create,
  });

  const { partialSubmission } = useSubmissionsWorkspace();

  const { items: stateComplianceItems } = useSearchStateComplianceByState(
    partialSubmission?.insuredPrimaryAddress?.state,
    mode === FormMode.create || mode === FormMode.edit,
  );

  const stateCompliance = stateComplianceItems?.[0];

  const hasMarketQuoteFileLabel = useMemo(
    () =>
      selectedFiles.some((file) => {
        const fileTypeLabel = getFileType(file);
        return !!fileTypeLabel && [FileType.MarketQuote, FileType.RenewalPolicy].includes(fileTypeLabel);
      }),
    [selectedFiles],
  );

  // used in the upload step to show alert banner and disable proceed button until there is a file selected with the right label according to the mode quote/binder/policy
  const isRequiredLabelMissing = useMemo(() => {
    if (fileType === QuoteFileIdType.MarketQuote) {
      // For MarketQuote, accept all quote file types
      return !selectedFiles.some((file) => {
        const fileTypeLabel = getFileType(file);
        return !!fileTypeLabel && quoteFilesTypes.includes(fileTypeLabel);
      });
    }

    return !selectedFiles.some((file) => getFileType(file) === quoteConfig.requiredFileType);
  }, [selectedFiles, quoteConfig.requiredFileType, fileType]);

  const [isPrimary, { set: setIsPrimary }] = useBoolean(
    !submission || (selectedLayer && !selectedLayer.attachmentPoint),
  );

  const { filesExplorer } = useUiStoreState();

  const [flowQuoteIds, setFlowQuoteIds] = useState<string[]>(selectedQuote?.capitolaQuoteFileIds || []);

  // only once working in marketQuote mode, the filesExplorer.selectedFiles represents the latest selected market quote files
  const marketQuoteFileIds =
    fileType === QuoteFileIdType.MarketQuote
      ? filesExplorer.selectedFiles.map((selectedFile) => selectedFile.id)
      : selectedQuote?.marketQuoteFileIds || [];

  const suggestions = useMemo(
    () =>
      Object.keys(appliedSuggestions).reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            ...appliedSuggestions[key],
            reason: <AIReason suggestion={appliedSuggestions[key]} />,
          },
        }),
        {},
      ),
    [appliedSuggestions],
  );

  const selectedProductId = watch(DetailsFieldsNames.InsuranceProductId);
  const selectedProduct = products.find((product) => product.id === selectedProductId);

  const isNonAdmittedProduct = selectedProduct?.isAdmitted === false;

  const addSubmissionMarketApi = async (
    addedMarket: Pick<UserMarket, 'id' | 'contacts'>,
    insuranceProductId?: string,
  ) =>
    addSubmissionMarket(
      {
        userMarketId: addedMarket.id,
        contactIds: addedMarket.contacts.map((contact) => contact.id),
        insuranceProductId,
      },
      false,
    );

  const createLayerApi = async (submissionId: string, attachmentPoint: number) =>
    createLayer({
      submissionId,
      attachmentPoint,
    });

  const createMarketIssuingCompanyApi = async (name: string, marketId: string) =>
    createMarketIssuingCompany({ name, marketId });

  const quoteFormProps: Omit<QuoteFormProps, 'submitForm' | 'quoteSubmitting' | 'setQuoteSubmitting'> = {
    fileType,
    boxFolderId: submission!.boxFolderId,
    fileIds: filesExplorer.selectedFiles.map((selectedFile) => selectedFile.id),
    submission: submission!,
    mode: mode!,
    submissionMarkets: markets,
    layers,
    quotes,
    selectedLayer: selectedLayer!,
    selectedQuote: selectedQuote!,
    addSubmissionMarketApi,
    createLayerApi,
    updateQuoteApi: updateQuote,
    createQuoteApi: createQuote,
    createMarketIssuingCompanyApi,
    updateSubmissionMarketRequestApi: updateSubmissionMarketRequest,
    onClose,
    setIsDirty,
    isPrimary,
    setIsPrimary,
    userMarkets,
    userMarketsLoading,
    currentStep,
    products,
    productsLoading: teamProductsLoading,
    initialProductId,
    submissionMarketRequestId: state.submissionMarketRequestId,
    flowQuoteIds,
    flowGenerationStatus,
    setFlowGenerationStatus,
    isRequiredLabelMissing,
    hasMarketQuoteFileLabel,
    insightPopulatedByAI,
    setInsightPopulatedByAI,
    marketQuoteFileIds,
    isDirty,
    suggestedValueProps: quoteConfig.suggestedValueProps,
    quoteConfig,
    stateCompliance,
    isNonAdmittedProduct,
    selectedMarket: state.selectedMarket,
  };

  const { submitForm, quoteSubmitting, setQuoteSubmitting } = useSubmitQuoteForm({
    ...quoteFormProps,
  });

  if (!submission) {
    return null;
  }

  return (
    <InnerPageLayout
      className="cap-quote-nested-view"
      title={quoteConfig.messages.title}
      sx={{ p: 0 }}
      subTitle={quoteConfig.messages.topHeaderSubtitle}
      {...(quoteConfig.showFooter && {
        footer: () => (
          <QuoteFooter
            {...quoteFormProps}
            submitForm={submitForm}
            quoteSubmitting={quoteSubmitting}
            setQuoteSubmitting={setQuoteSubmitting}
          />
        ),
      })}
    >
      {() => (
        <QuoteFormFields
          brandingMode={state.brandingMode}
          boxFolderId={submission!.boxFolderId}
          submission={submission!}
          mode={mode!}
          submissionMarkets={markets}
          layers={layers}
          quotes={quotes}
          selectedLayer={selectedLayer!}
          selectedQuote={selectedQuote!}
          addSubmissionMarketApi={addSubmissionMarketApi}
          createLayerApi={createLayerApi}
          updateQuoteApi={updateQuote}
          createQuoteApi={createQuote}
          setIsDirty={setIsDirty}
          isPrimary={isPrimary}
          setIsPrimary={setIsPrimary}
          userMarkets={userMarkets}
          userMarketsLoading={userMarketsLoading}
          fileType={fileType}
          currentStep={currentStep}
          products={products}
          productsLoading={teamProductsLoading}
          initialProductId={initialProductId}
          marketQuoteFileIds={marketQuoteFileIds}
          flowQuoteIds={flowQuoteIds}
          setFlowQuoteIds={setFlowQuoteIds}
          flowGenerationStatus={flowGenerationStatus}
          setFlowGenerationStatus={setFlowGenerationStatus}
          isRequiredLabelMissing={isRequiredLabelMissing}
          hasMarketQuoteFileLabel={hasMarketQuoteFileLabel}
          extractedDataSuggestions={suggestions}
          setInsightPopulatedByAI={setInsightPopulatedByAI}
          isDirty={isDirty}
          quoteConfig={quoteConfig}
          stateCompliance={stateCompliance}
          isNonAdmittedProduct={isNonAdmittedProduct}
          marketName={selectedProduct?.marketName ?? ''}
          quoteSubmitting={quoteSubmitting}
          setQuoteSubmitting={setQuoteSubmitting}
          submitForm={submitForm}
          onClose={onClose}
        />
      )}
    </InnerPageLayout>
  );
}
